import React, { useState, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useModuleList } from '../../../common/API/hooks';
import ApiClient from '../../../common/API';
import {
    Box, Typography, Stack, Chip, Button, Paper, CircularProgress, Divider,
} from '@mui/material';
import { toast } from 'react-toastify';
import handleError from '../ErrorHandling';
import {
    dashboardPaths,
    adminPaths,
    featurePaths,
    experimentalPaths,
} from './modulePathCatalog';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';


const CATEGORY_META = {
    dashboard:    { label: 'Dashboard',    color: 'primary' },
    admin:        { label: 'Admin',        color: 'secondary' },
    feature:      { label: 'Feature',      color: 'info' },
    experimental: { label: 'Experimental', color: 'default' },
};

export default function UnregisteredModules() {
    const queryClient = useQueryClient();
    const { data: moduleList, isLoading: moduleLoading } = useModuleList();
    const [pendingPaths, setPendingPaths] = useState(new Set());

    const { mutate: registerModule } = useMutation(
        (path) => ApiClient().post('module/', { name: path }).then(r => r.data),
        {
            onSuccess: (_, path) => {
                queryClient.invalidateQueries(['module']).then(() => {
                    setPendingPaths(prev => {
                        const next = new Set(prev);
                        next.delete(path);
                        return next;
                    });
                    toast.success(`Module "${path}" registered!`);
                });
            },
            onError: (error, path) => {
                setPendingPaths(prev => {
                    const next = new Set(prev);
                    next.delete(path);
                    return next;
                });
                toast.error(handleError(error, 'Module', 'save'));
            },
        },
    );

    const handleRegister = (path) => {
        if (pendingPaths.has(path)) return;
        setPendingPaths(prev => new Set([...prev, path]));
        registerModule(path);
    };

    // Group unregistered paths by category
    const { grouped, totalCount } = useMemo(() => {
        const registeredPaths = (moduleList || []).map(row => row.name);
        const isUnregistered = (p) => !registeredPaths.includes(p);

        const groups = [
            { key: 'dashboard',    paths: dashboardPaths.filter(isUnregistered) },
            { key: 'admin',        paths: adminPaths.filter(isUnregistered) },
            { key: 'feature',      paths: featurePaths.filter(isUnregistered) },
            { key: 'experimental', paths: experimentalPaths.filter(isUnregistered) },
        ].filter(g => g.paths.length > 0);

        const totalCount = groups.reduce((sum, g) => sum + g.paths.length, 0);
        return { grouped: groups, totalCount };
    }, [moduleList]);

    if (moduleLoading) {
        return (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">Checking for unregistered modules…</Typography>
                </Stack>
            </Paper>
        );
    }

    if (!moduleList) return null;

    if (totalCount === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#81C784' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckCircleRoundedIcon sx={{ color: '#2E7D32', fontSize: '1.2rem' }} />
                    <Typography variant="body2" color="text.secondary">
                        All detected modules are registered.
                    </Typography>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: '#FFB74D', bgcolor: '#FFFBF5' }}>
            {/* Header */}
            <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
                <WarningAmberRoundedIcon sx={{ color: '#ED6C02', fontSize: '1.3rem', mt: 0.2 }} />
                <Box>
                    <Typography variant="subtitle2" fontWeight={700} color="#ED6C02">
                        {totalCount} Unregistered Module{totalCount !== 1 ? 's' : ''} Detected
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        These routes exist in the app but are not in the permission registry.
                        Register them to control access. Experimental routes can safely be skipped.
                    </Typography>
                </Box>
            </Stack>

            {/* Grouped sections */}
            <Stack spacing={2}>
                {grouped.map((group, gi) => {
                    const meta = CATEGORY_META[group.key];
                    return (
                        <Box key={group.key}>
                            {gi > 0 && <Divider sx={{ mb: 1.5 }} />}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                <Chip
                                    label={meta.label}
                                    size="small"
                                    color={meta.color}
                                    sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {group.paths.length} path{group.paths.length !== 1 ? 's' : ''}
                                </Typography>
                            </Stack>
                            <Stack spacing={0.6}>
                                {group.paths.map(path => {
                                    const isPending = pendingPaths.has(path);
                                    return (
                                        <Stack
                                            key={path}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                px: 1.5, py: 0.75, borderRadius: 1.5,
                                                bgcolor: 'white', border: '1px solid #FFE0B2',
                                                opacity: isPending ? 0.65 : 1,
                                                transition: 'opacity 0.2s',
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                {path}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={isPending}
                                                startIcon={isPending
                                                    ? <CircularProgress size={12} sx={{ color: '#ED6C02' }} />
                                                    : <AddCircleOutlineRoundedIcon />}
                                                sx={{
                                                    textTransform: 'none', fontSize: '0.75rem', flexShrink: 0, ml: 1,
                                                    color: '#ED6C02', borderColor: '#FFB74D',
                                                    '&:hover': { bgcolor: '#FFF3E0', borderColor: '#ED6C02' },
                                                }}
                                                onClick={() => handleRegister(path)}
                                            >
                                                {isPending ? 'Registering…' : 'Register'}
                                            </Button>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>
        </Paper>
    );
}

